import {
  useCreateJobTag,
  useDeleteJobTag,
  useJobTags,
  useUpdateJobTag,
} from '@/api/Jobs';
import { useTranslate } from '@/translations';
import { JobTag } from '@/types';
import { faEdit, faPlus, faTrash, faTrashArrowUp, Icon } from '@/ui/components';
import { App, Button, Empty, Input, Space, Spin, Tag, Typography } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { JobTagPopup } from '../JobTagPopup';

export const JobTagManagement: FC = () => {
  const { t } = useTranslate();
  const { modal } = App.useApp();

  const { data: jobTags, isFetching: isJobTagsLoading } = useJobTags();
  const { mutateAsync: createMutateAsync } = useCreateJobTag();
  const { mutateAsync: updateMutateAsync } = useUpdateJobTag();
  const { mutateAsync: deleteMutateAsync } = useDeleteJobTag();

  const [jobTag, setJobTag] = useState<JobTag>();
  const [keyword, setKeyword] = useState('');

  const isLoading = isJobTagsLoading;

  const onSubmit = async (data: JobTag) => {
    const res = await (data.id
      ? updateMutateAsync(data)
      : createMutateAsync(data));
    return res.message?.Message;
  };

  const onDelete = useCallback(
    (id: number, unDelete?: boolean) => {
      modal.confirm({
        title: 'Confirm',
        content: t(
          unDelete ? 'unDeleteRecordQuestion' : 'deleteRecordQuestion',
        ),
        okText: t('yes'),
        onOk: () => deleteMutateAsync({ id, unDelete }),
        onCancel: () => {},
      });
    },
    [deleteMutateAsync, modal, t],
  );

  const { activeTags, deletedTags } = useMemo(() => {
    const list = jobTags ?? [];
    const active = list.filter((tag) => !tag.deleted);
    const deleted = list.filter((tag) => tag.deleted);
    const byName = (a: JobTag, b: JobTag) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    active.sort(byName);
    deleted.sort(byName);
    return { activeTags: active, deletedTags: deleted };
  }, [jobTags]);

  const normalizedKeyword = keyword.trim().toLowerCase();

  const filteredActiveTags = useMemo(
    () =>
      normalizedKeyword
        ? activeTags.filter((tag) =>
            tag.name.toLowerCase().includes(normalizedKeyword),
          )
        : activeTags,
    [activeTags, normalizedKeyword],
  );

  const filteredDeletedTags = useMemo(
    () =>
      normalizedKeyword
        ? deletedTags.filter((tag) =>
            tag.name.toLowerCase().includes(normalizedKeyword),
          )
        : deletedTags,
    [deletedTags, normalizedKeyword],
  );

  return (
    <>
      <Spin spinning={isLoading}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h3 className="m-0">Job tags</h3>

            <div className="d-flex align-items-center gap-2">
              <Input.Search
                size="large"
                allowClear
                placeholder={t('search')}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                style={{ maxWidth: 320 }}
              />
              <Button
                shape="circle"
                icon={<Icon icon={faPlus} />}
                onClick={() => setJobTag({ name: '' } as JobTag)}
              />
            </div>
          </div>

          <div>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 8 }}
            >
              {t('active')}
            </Typography.Text>
            {filteredActiveTags.length === 0 ? (
              <Empty />
            ) : (
              <Space wrap size={[8, 8]}>
                {filteredActiveTags.map((tag) => (
                  <Tag
                    key={tag.id}
                    color="blue"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '2px 4px',
                    }}
                  >
                    <span>{tag.name}</span>
                    <div className="ms-2">
                      <Button
                        type="text"
                        size="small"
                        aria-label={t('edit')}
                        color="green"
                        icon={<Icon icon={faEdit} />}
                        onClick={() => setJobTag({ ...tag })}
                      />
                      <Button
                        type="text"
                        size="small"
                        danger
                        aria-label={t('delete')}
                        icon={<Icon icon={faTrash} />}
                        onClick={() => onDelete(tag.id)}
                      />
                    </div>
                  </Tag>
                ))}
              </Space>
            )}
          </div>

          {filteredDeletedTags.length > 0 ? (
            <div>
              <Typography.Text
                type="secondary"
                style={{ display: 'block', marginBottom: 8 }}
              >
                {t('deletedTags')}
              </Typography.Text>
              <Space wrap size={[8, 8]}>
                {filteredDeletedTags.map((tag) => (
                  <Tag
                    key={tag.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      margin: 0,
                      opacity: 0.85,
                    }}
                  >
                    <span style={{ textDecoration: 'line-through' }}>
                      {tag.name}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      icon={<Icon icon={faTrashArrowUp} />}
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => onDelete(tag.id, true)}
                    />
                  </Tag>
                ))}
              </Space>
            </div>
          ) : null}
        </Space>
      </Spin>

      <JobTagPopup
        data={jobTag}
        onCancel={() => setJobTag(undefined)}
        onSubmit={onSubmit}
      />
    </>
  );
};
